/**
 * HTTP client wrapper. All calls go through the backend proxy
 * — never directly to N8N/WP/OpenAI from the browser.
 *
 * Auth: attaches the Bearer JWT from the auth store on every request.
 * On a 401 from a non-login route, the session is cleared and the user
 * is bounced to /login (expired/invalid token).
 */
import { useAuth } from '@/store/useAuth';

export const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

/**
 * Toda falha de rede vira ALARME GLOBAL (canto superior direito), venha de que
 * tela vier. Antes cada tela escrevia em vermelho no seu canto — quem estava
 * noutra aba não via que o crédito da IA tinha acabado, e descobria pela
 * matéria que não saiu.
 *
 * Import dinâmico do store para não criar ciclo (o store não depende daqui,
 * mas quem importa `api` cedo no boot pode não ter o zustand pronto).
 */
export function tituloDaFalha(status: number, body: unknown): string {
  const detalhe = String((body as { detail?: unknown })?.detail ?? '').toLowerCase();
  // 400 é RECUSA DE VALIDAÇÃO — o que o operador digitou não passou. Nunca é
  // cobrança, e ler a palavra "crédito" no meio de uma mensagem de validação
  // como falta de saldo é o defeito que isto fecha: salvar os parâmetros do
  // CRÉDITO com JSON quebrado anunciava "Créditos da IA esgotados", e o
  // operador ia abrir chamado de faturamento por uma vírgula faltando
  // (medido na tela em 12/09/2026). O detalhe já diz o motivo real.
  if (status === 400) return 'Parâmetro recusado';
  if (status === 402 || detalhe.includes('quota') || detalhe.includes('cota'))
    return 'Teto de uso atingido';
  // Termos de COBRANÇA, não a palavra solta: "crédito" aparece em texto
  // editorial legítimo (o crédito do repórter).
  if (
    detalhe.includes('insufficient') ||
    detalhe.includes('billing') ||
    detalhe.includes('sem crédito') ||
    detalhe.includes('sem credito') ||
    detalhe.includes('credit balance') ||
    detalhe.includes('creditos esgotados') ||
    detalhe.includes('créditos esgotados')
  )
    return 'Créditos da IA esgotados';
  if (detalhe.includes('rate limit') || status === 429) return 'Provedor limitou a taxa (429)';
  if (status === 403) return 'Sem permissão para esta ação';
  if (status === 404) return 'Recurso não encontrado';
  if (status === 409) return 'Conflito de destino';
  // 503 é o código que os endpoints usam para dizer "o FORNECEDOR está fora"
  // (SerpApi, LLM, WordPress). Chamar isso de "Falha no servidor" mandava o
  // operador abrir chamado contra nós por um problema de terceiro — foi o que
  // a tela disse durante o incidente do Google News em 05/09/2026.
  if (status === 502 || status === 503 || status === 504)
    return 'Serviço externo não respondeu';
  if (status >= 500) return 'Falha no servidor';
  return `Falha ${status}`;
}

function reportarFalha(status: number, body: unknown, path: string): void {
  // 401 já é tratado (sessão expirada → login). 422 é validação editorial/orientação tratada pela própria tela.
  if (status === 401 || status === 422) return;
  const d = (body as { detail?: unknown })?.detail;
  const detalhe = typeof d === 'string' ? d : d ? JSON.stringify(d).slice(0, 240) : path;
  import('@/store/useAlertas')
    .then(({ alertar }) => alertar('erro', tituloDaFalha(status, body), detalhe))
    .catch(() => {
      /* alarme é best-effort: nunca pode mascarar o erro original */
    });
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * T5.3 — o access token vive em MEMÓRIA (ver `store/useAuth.ts`), então some a
 * cada F5. Quem devolve a sessão é o refresh token, que o backend guarda num
 * cookie HttpOnly: o JavaScript não o lê, mas o navegador o envia sozinho para
 * `/auth/refresh`.
 *
 * Uma promessa COMPARTILHADA de propósito. Uma tela típica dispara várias
 * chamadas ao montar; sem isto, cada uma pediria o seu refresh — e como o
 * refresh é ROTATIVO (o backend queima o token usado a cada troca), a segunda
 * chegaria com um token já gasto, o backend leria aquilo como reuso/roubo e
 * mataria a família inteira. O resultado seria uma sessão que cai justamente
 * quando a página carrega depressa.
 */
let renovacaoEmCurso: Promise<boolean> | null = null;

export async function renovarSessao(): Promise<boolean> {
  if (!renovacaoEmCurso) {
    renovacaoEmCurso = (async () => {
      try {
        const r = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        });
        if (!r.ok) return false;
        const dados = (await r.json()) as { access_token?: string; role?: string };
        if (!dados.access_token) return false;
        useAuth.getState().setToken(dados.access_token);
        return true;
      } catch {
        return false;
      } finally {
        // Liberado no próximo tick para que os chamadores concorrentes desta
        // rodada compartilhem o MESMO resultado antes de alguém tentar de novo.
        queueMicrotask(() => {
          renovacaoEmCurso = null;
        });
      }
    })();
  }
  return renovacaoEmCurso;
}

/** Rotas que nunca disparam renovação: são elas próprias o caminho da sessão. */
function ehRotaDeSessao(path: string): boolean {
  return path.startsWith('/auth/login') || path.startsWith('/auth/refresh');
}

function derrubarSessao(): void {
  useAuth.getState().logout();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

/**
 * Status que a TELA ja trata e que, portanto, nao devem virar alarme global.
 * Ex.: GET de configuracao que ainda nao existe responde 404 — 'nunca
 * configurado' e estado inicial normal, nao falha. Sem isto o operador abria
 * /config/seo e /config/publicacao e levava um toast VERMELHO de erro numa
 * tela que estava perfeita (medido em producao 27/08).
 */
export type OpcoesApi = { esperados?: readonly number[] };

/** Mesmo fetch autenticado do apiFetch, mas devolve a Response crua (binário —
 * ex.: imagem servida pelo proxy de mídia). Lança ApiError em !ok. */
export async function apiFetchRaw(
  path: string,
  init: RequestInit = {},
  opcoes: OpcoesApi = {},
): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  // BOOT: recarregou a página, o token de memória sumiu, mas a identidade
  // persistida diz que havia sessão — então há um cookie de refresh para
  // trocar. Sem isto, todo F5 mandaria o operador de volta para o /login, e o
  // preço de tirar o token do disco seria pago em usabilidade.
  if (!useAuth.getState().token && useAuth.getState().username && !ehRotaDeSessao(path)) {
    await renovarSessao();
  }

  const disparar = (): Promise<Response> => {
    const token = useAuth.getState().token;
    // ORDEM IMPORTA: `...init` precisa vir ANTES de `headers`. Na forma antiga
    // (`headers` primeiro, `...init` depois), qualquer caller que passasse
    // `headers: {}` — o caso dos uploads com FormData — substituía o objeto
    // inteiro e a requisição saía SEM Authorization: o backend devolvia 401 e o
    // interceptor abaixo derrubava a sessão ("anexar PDF desloga").
    // FormData também não pode levar Content-Type manual (o navegador precisa
    // pôr o boundary), então o JSON default só entra quando o body não é FormData.
    const ehFormData = init.body instanceof FormData;
    return fetch(url, {
      credentials: 'include',
      ...init,
      headers: {
        ...(ehFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
    });
  };

  let response = await disparar();

  // Sessão expirada/inválida — mas nunca no próprio login (a tela de login
  // mostra o seu erro) nem no refresh (aí o 401 é a resposta final).
  if (response.status === 401 && !ehRotaDeSessao(path)) {
    // UMA tentativa de renovar antes de deslogar. O access agora dura 15 min:
    // sem esta retentativa, o operador seria expulso a cada quarto de hora no
    // meio de uma edição.
    if (await renovarSessao()) {
      response = await disparar();
    }
    if (response.status === 401) {
      derrubarSessao();
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    if (!opcoes.esperados?.includes(response.status)) {
      reportarFalha(response.status, body, path);
    }
    throw new ApiError(response.status, body, `${response.status} ${response.statusText}`);
  }

  return response;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  opcoes: OpcoesApi = {},
): Promise<T> {
  const response = await apiFetchRaw(path, init, opcoes);

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function apiStream(path: string, init: RequestInit = {}): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;

  // Mesmo boot do apiFetch: um stream aberto logo depois de um F5 encontraria
  // a memória vazia e sairia sem Authorization.
  if (!useAuth.getState().token && useAuth.getState().username && !ehRotaDeSessao(path)) {
    await renovarSessao();
  }

  const disparar = (): Promise<Response> => {
    const token = useAuth.getState().token;
    return fetch(url, {
      credentials: 'include',
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
      ...init,
    });
  };

  let response = await disparar();
  if (response.status === 401 && !ehRotaDeSessao(path) && (await renovarSessao())) {
    response = await disparar();
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    reportarFalha(response.status, body, path);
    throw new ApiError(response.status, body, `${response.status} ${response.statusText}`);
  }
  return response;
}

export const api = {
  get: <T>(path: string, opcoes?: OpcoesApi) => apiFetch<T>(path, { method: 'GET' }, opcoes),
  // `opcoes.esperados` também nos verbos de escrita: uma sonda/side-effect com
  // status previsto (ex.: PUT /config/locale que responde 400 para staff por
  // desenho) não deve virar alarme global (F-486).
  post: <T>(path: string, body?: unknown, opcoes?: OpcoesApi) =>
    apiFetch<T>(
      path,
      {
        method: 'POST',
        body: body === undefined ? undefined : JSON.stringify(body),
      },
      opcoes,
    ),
  put: <T>(path: string, body?: unknown, opcoes?: OpcoesApi) =>
    apiFetch<T>(
      path,
      {
        method: 'PUT',
        body: body === undefined ? undefined : JSON.stringify(body),
      },
      opcoes,
    ),
  patch: <T>(path: string, body?: unknown, opcoes?: OpcoesApi) =>
    apiFetch<T>(
      path,
      {
        method: 'PATCH',
        body: body === undefined ? undefined : JSON.stringify(body),
      },
      opcoes,
    ),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
